import { gql, type TypedDocumentNode } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button/index.tsx';
import { CopyToClipboardButton } from '@sorare/core/src/atoms/buttons/CopyToClipboardButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconDuplicate } from '@sorare/core/src/atoms/icons/system/iconDuplicate.ts';
import { Portal } from '@sorare/core/src/atoms/layout/Portal/index.tsx';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { BodyM, BodyS, LabelL } from '@sorare/core/src/atoms/typography';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { SocialShare } from '@sorare/core/src/components/user/SocialShare/index.tsx';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop.ts';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery.ts';
import {
  socialShareEventContext,
  socialShareEventName,
} from '@sorare/core/src/lib/events.ts';
import { glossary, navLabels } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { InviteContent } from '#src/components/series/inviteFriends/InviteDialog/InviteContent/index.tsx';
import { RewardsContent } from '#src/components/series/inviteFriends/InviteDialog/RewardsContent/index.tsx';

import type {
  SetReferralOnlineLandingQuery,
  SetReferralOnlineLandingQueryVariables,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical)`
  position: relative;
  padding-top: var(--double-unit);
  @media ${laptopAndAbove} {
    padding-top: calc(6 * var(--unit));
  }
`;
const BackgroundWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  & * {
    width: 100%;
    height: 100%;
  }
  &:after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 40%, var(--c-black) 100%);
  }
`;

const TabBarWrapper = styled.div`
  align-self: center;
`;
const ContentGrid = styled.div`
  min-height: calc(100vh - var(--current-stack-height));
  @media ${laptopAndAbove} {
    display: grid;
    grid-template-columns: 1fr 400px;
  }
`;
const LeftPanel = styled(Vertical).attrs({ gap: 4, center: true })`
  padding: var(--intermediate-unit) var(--double-unit);
`;
const RightPanel = styled(Vertical).attrs({ gap: 3 })`
  padding: var(--intermediate-unit) var(--double-unit);
  @media ${laptopAndAbove} {
    border-left: 1px solid var(--c-nd-200);
  }
`;
const ReferralSection = styled(Vertical)`
  background-color: var(--c-nd-100);
  padding: var(--unit);
  border-radius: 8px;
  width: 100%;
  @media ${laptopAndAbove} {
    flex-direction: row;
  }
`;
const ReferralUrlText = styled(BodyM)`
  flex: 1;
  white-space: nowrap;
`;

const FadedSpan = styled.span`
  color: var(--c-nd-600);
`;

const SET_REFERRAL_ONLINE_LANDING_QUERY = gql`
  query SetReferralOnlineLandingQuery($sport: Sport!) {
    cardSet {
      id
      maximumInviteTasksAsReferrer(sport: $sport)
      ...InviteContent_cardSetRoot
      ...RewardsContent_cardSetRoot
    }
    currentUser {
      slug
      referralUrl
      inviteTasksAsReferrer(sport: $sport) {
        id
      }
    }
  }
  ${InviteContent.fragments.cardSetRoot}
  ${RewardsContent.fragments.cardSetRoot}
` as TypedDocumentNode<
  SetReferralOnlineLandingQuery,
  SetReferralOnlineLandingQueryVariables
>;

export const SetReferralOnlineLanding = () => {
  const { sport } = useSportContext();
  const isDesktop = useIsLaptop();
  const [tab, setTab] = useState<'invite' | 'rewards'>('invite');

  const { data, loading } = useQuery(SET_REFERRAL_ONLINE_LANDING_QUERY, {
    variables: { sport },
  });

  const { currentUser, cardSet } = data || {};
  const { maximumInviteTasksAsReferrer } = cardSet || {};
  const { inviteTasksAsReferrer, referralUrl } = currentUser || {};

  const friendsRemaining =
    (maximumInviteTasksAsReferrer || 10) - (inviteTasksAsReferrer?.length || 0);

  const shareButton = (
    <SocialShare
      url={referralUrl}
      renderButton={({ onClick }) => (
        <Button size="large" color="primary" onClick={onClick}>
          <FormattedMessage
            defaultMessage="Invite friends <span>({friendsRemaining, number} left)</span>"
            values={{
              friendsRemaining,
              span: chunks => <FadedSpan>{chunks}</FadedSpan>,
            }}
          />
        </Button>
      )}
      trackingEventName={socialShareEventName.SHARE_INVITE_FRIENDS_SERIES}
      trackingEventContext={socialShareEventContext.SERIES_INVITE_FRIENDS}
    />
  );

  return (
    <Root>
      <Portal id="page-background">
        <BackgroundWrapper>
          <TextureBackground
            background="wave1"
            mode="dark"
            color="var(--c-set-main-color)"
          />
        </BackgroundWrapper>
      </Portal>
      <PageHeader text={navLabels.inviteFriends} disableBackBrowser />
      {!isDesktop && (
        <TabBarWrapper>
          <TabBar variant="primary" value={tab}>
            <Tab value="invite" onClick={() => setTab('invite')}>
              <FormattedMessage {...glossary.invite} />
            </Tab>
            <Tab value="rewards" onClick={() => setTab('rewards')}>
              <FormattedMessage {...glossary.rewards} />
            </Tab>
          </TabBar>
        </TabBarWrapper>
      )}
      <ContentGrid>
        {!!(isDesktop || tab === 'invite') && (
          <LeftPanel>
            <InviteContent cardSetRoot={cardSet} loading={loading}>
              <Vertical gap={2} center style={{ width: '100%' }}>
                <ReferralSection>
                  <Horizontal style={{ flex: 1 }}>
                    <ReferralUrlText>{referralUrl}</ReferralUrlText>
                    {!!referralUrl && (
                      <CopyToClipboardButton textToCopy={referralUrl}>
                        <FontAwesomeIcon
                          icon={iconDuplicate}
                          color="var(--c-white)"
                        />
                      </CopyToClipboardButton>
                    )}
                  </Horizontal>
                  {shareButton}
                </ReferralSection>
              </Vertical>
            </InviteContent>
          </LeftPanel>
        )}
        {!!(isDesktop || tab === 'rewards') && (
          <RightPanel>
            <Vertical gap={3} style={{ minHeight: '50vh' }}>
              <Vertical gap={1.5}>
                <LabelL bold>
                  <FormattedMessage {...glossary.rewards} />
                </LabelL>
                <BodyS color="var(--c-nd-600)">
                  <FormattedMessage defaultMessage="Earn extra rewards as your friends progress" />
                </BodyS>
              </Vertical>
              <RewardsContent cardSetRoot={cardSet} />
            </Vertical>
          </RightPanel>
        )}
      </ContentGrid>
    </Root>
  );
};
